import { create } from 'zustand'

interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  body?: string
  duration?: number
}

interface UiState {
  sidebarCollapsed: boolean
  commandPaletteOpen: boolean
  composerOpen: boolean
  toasts: Toast[]

  setSidebarCollapsed: (collapsed: boolean) => void
  toggleSidebar: () => void
  openCommandPalette: () => void
  closeCommandPalette: () => void
  openComposer: () => void
  closeComposer: () => void
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
}

export const useUiStore = create<UiState>((set) => ({
  sidebarCollapsed: false,
  commandPaletteOpen: false,
  composerOpen: false,
  toasts: [],

  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

  openCommandPalette: () => set({ commandPaletteOpen: true }),
  closeCommandPalette: () => set({ commandPaletteOpen: false }),

  openComposer: () => set({ composerOpen: true }),
  closeComposer: () => set({ composerOpen: false }),

  addToast: (toast) => {
    const id = crypto.randomUUID()
    set((s) => ({ toasts: [...s.toasts, { ...toast, id }] }))
    // Auto-remove after duration
    const duration = toast.duration ?? 4000
    setTimeout(() => {
      set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }))
    }, duration)
  },

  removeToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}))

// Convenience hook
export function useToast() {
  const addToast = useUiStore((s) => s.addToast)
  return {
    success: (title: string, body?: string) => addToast({ type: 'success', title, ...(body !== undefined ? { body } : {}) }),
    error: (title: string, body?: string) => addToast({ type: 'error', title, ...(body !== undefined ? { body } : {}) }),
    warning: (title: string, body?: string) => addToast({ type: 'warning', title, ...(body !== undefined ? { body } : {}) }),
    info: (title: string, body?: string) => addToast({ type: 'info', title, ...(body !== undefined ? { body } : {}) }),
  }
}
